import * as nipplejs from 'nipplejs';
import App from '../App';

import { Person, RotationDirections, MoveSpeed } from './Person';

interface JoystickMoveActions {
    dirTimer: number | undefined;
    angle: number;
    speed: MoveSpeed;
    rotTimer: number | undefined;
    rotation: RotationDirections;
}

export enum StickVisibility {
    DISABLED,
    MOVE_ONLY,
    FULL
}

export class VirtualJoystick {
    public readonly app: App;
    private leftDiv: HTMLDivElement;
    private rightDiv: HTMLDivElement;
    private leftVirtualJoystick: nipplejs.JoystickManager;
    private rightVirtualJoystick: nipplejs.JoystickManager;
    private joystickMoveActions: JoystickMoveActions;
    private stickVisibility: StickVisibility;

    public set StickVisibility(visibility: StickVisibility) {
        if (this.stickVisibility !== visibility) {
            this.stickVisibility = visibility;
            this.updateJoystickVisibiliy();
        }
    }
    public get StickVisibility(): StickVisibility {
        return this.stickVisibility;
    }

    constructor(app: App, stickVisibility: StickVisibility) {
        this.app = app;
        // const viewerContainer: HTMLElement | null = document.getElementById('viewer-container');
        const viewerContainer: HTMLElement | null = this.app.coreApp.viewer.container; // document.getElementById('viewer-container');
        if (viewerContainer === null) {
            return;
        }

        this.stickVisibility = stickVisibility;

        // create joystick divs
        this.leftDiv = this.createJoystickDiv('left-joystick');
        this.leftDiv.style.left = '0';
        viewerContainer.appendChild(this.leftDiv);

        this.rightDiv = this.createJoystickDiv('right-joystick');
        this.rightDiv.style.right = '0';
        viewerContainer.appendChild(this.rightDiv);

        // interface with relevant move and rotation data
        this.joystickMoveActions = {
            dirTimer: undefined,
            angle: 0,
            speed: 'idle',
            rotTimer: undefined,
            rotation: 'idle'
        };

        // initialize joysticks
        this.createLeftJoystick();
        this.createRightJoystick();
        this.updateJoystickVisibiliy();
        this.app.coreApp.model.scene.registerBeforeRender(this.handleJoystickEvents);
    }

    private createJoystickDiv(id: string): HTMLDivElement {
        const div: HTMLDivElement = document.createElement('div');
        div.id = id;
        div.style.width = '50%';
        div.style.height = '100%';
        div.style.bottom = '0';
        div.style.position = 'absolute';
        div.style.display = 'block';

        div.setAttribute('touch-action', 'none');

        return div;
    }

    private createLeftJoystick(): void {
        const leftStickOptions: nipplejs.JoystickManagerOptions = {
            zone: this.leftDiv,
            mode: 'dynamic',
            restOpacity: 1,
            color: 'grey',
            fadeTime: 0
        };
        this.leftVirtualJoystick = nipplejs.create(leftStickOptions);

        this.leftVirtualJoystick.on('start', (event: nipplejs.EventData, data: nipplejs.JoystickOutputData): void => {
            Person.doNothing(this.app.coreApp.viewer.view);
        });
        this.leftVirtualJoystick.on('move', (event: nipplejs.EventData, data: nipplejs.JoystickOutputData): void => {
            if (!data.direction || !data.direction.angle) {
                return;
            }
            this.joystickMoveActions.angle = this.getAdjustedAngle(data.angle.degree);
            this.joystickMoveActions.speed = this.getSpeedByForce(data.force);
        });
        this.leftVirtualJoystick.on('end', (event: nipplejs.EventData, data: nipplejs.JoystickOutputData): void => {
            this.joystickMoveActions.speed = 'idle';
            this.joystickMoveActions.angle = 0;
        });
    }

    private createRightJoystick(): void {
        const rightStickOptions: nipplejs.JoystickManagerOptions = {
            zone: this.rightDiv,
            mode: 'dynamic',
            restOpacity: 1,
            color: 'grey',
            fadeTime: 0
        };
        this.rightVirtualJoystick = nipplejs.create(rightStickOptions);

        this.rightVirtualJoystick.on('start', (event: nipplejs.EventData, data: nipplejs.JoystickOutputData): void => {
            Person.doNothing(this.app.coreApp.viewer.view);
        });
        this.rightVirtualJoystick.on('move', (event: nipplejs.EventData, data: nipplejs.JoystickOutputData): void => {
            if (!data.direction || !data.direction.angle) {
                return;
            }
            this.joystickMoveActions.rotation = this.getRotationDirection(data.direction.angle);
        });
        this.rightVirtualJoystick.on('end', (event: nipplejs.EventData, data: nipplejs.JoystickOutputData): void => {
            this.joystickMoveActions.rotation = 'idle';
        });
    }

    private updateJoystickVisibiliy(): void {
        if (this.stickVisibility === StickVisibility.DISABLED) {
            this.leftDiv.style.display = 'none';
            this.rightDiv.style.display = 'none';
            return;
        }
        if (this.stickVisibility === StickVisibility.FULL) {
            this.leftDiv.style.width = '50%';
            this.leftDiv.style.display = 'block';
            this.rightDiv.style.display = 'block';
            return;
        }
        this.leftDiv.style.width = '100%';
        this.leftDiv.style.display = 'block';
        this.rightDiv.style.display = 'none';
    }

    private handleJoystickEvents = (): void => {
        if (!this.app.coreApp.viewer.view) {
            return;
        }
        if (this.joystickMoveActions.speed !== 'idle') {
            Person.move(this.app.coreApp.viewer.view, this.joystickMoveActions.angle, this.joystickMoveActions.speed);
        }
        if (this.joystickMoveActions.rotation !== 'idle') {
            Person.rotate(this.app.coreApp.viewer.view, this.joystickMoveActions.rotation);
        }
    };

    private getRotationDirection(value: string): RotationDirections {
        if (value === 'up') {
            return 'top';
        }
        if (value === 'down') {
            return 'down';
        }
        if (value === 'left') {
            return 'left';
        }
        if (value === 'right') {
            return 'right';
        }
        return 'idle';
    }

    private getAdjustedAngle(angle: number): number {
        const newAngle: number =  Math.floor((angle * -1) + 90);
        if (newAngle <= -180) {
            return (newAngle + 360);
        }
        return newAngle;
    }

    private getSpeedByForce(force: number): MoveSpeed {
        if (force < 0.1) {
            return 'idle';
        }
        if (force < 0.5) {
            return 'low';
        }
        if (force < 1.5) {
            return 'mid';
        }
        return 'high';
    }
}